// =============================================================================
// Portal App Root — Routes, Auth, Query Client
// =============================================================================

import { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiClientProvider } from '@tkpod/api-client';
import { PortalLayout } from './layouts/PortalLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { DashboardPage } from './pages/DashboardPage';
import { PortalOrdersPage } from './pages/PortalOrdersPage';
import { PortalOrderDetailPage } from './pages/PortalOrderDetailPage';
import { CreateOrderPage } from './pages/CreateOrderPage';
import { ProductsPage } from './pages/ProductsPage';
import { MyProductDetailPage } from './pages/MyProductDetailPage';
import { MyDesignsPage } from './pages/MyDesignsPage';
import { MyDesignDetailPage } from './pages/MyDesignDetailPage';
import { CatalogPage } from './pages/CatalogPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { StoresPage } from './pages/StoresPage';
import {
  PortalBillingPage,
  BILLING_PAGE_PERMISSIONS,
  type BillingTab,
} from './pages/PortalBillingPage';
import { SettingsPage } from './pages/SettingsPage';
import { FulfillmentSettingsPage } from './pages/FulfillmentSettingsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { NotificationPreferencesPage } from './pages/NotificationPreferencesPage';
import { OrganizationSettingsPage } from './pages/OrganizationSettingsPage';
import { BrandingSettingsPage } from './pages/BrandingSettingsPage';
import { DesignerSettingsPage } from './pages/DesignerSettingsPage';
import { TeamPage } from './pages/TeamPage';
import { ProfilePage } from './pages/ProfilePage';
import { MemberDetailPage } from './pages/MemberDetailPage';
import { JoinPage } from './pages/JoinPage';
import { SsoShopifyPage } from './pages/SsoShopifyPage';
import { useAuthStore } from './lib/auth-store';
import { api } from './lib/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Legacy billing route redirect — forwards to /billing?tab=... while
 * preserving every other query param (Stripe return statuses like
 * `?topup=success`, list filters, `?changed=1`, etc.).
 */
function BillingTabRedirect({
  tab,
  extra,
}: {
  tab: BillingTab;
  extra?: Record<string, string>;
}) {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('tab', tab);
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (!next.has(key)) next.set(key, value);
    }
  }
  return <Navigate to={`/billing?${next.toString()}`} replace />;
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    if (status === 'idle') {
      void useAuthStore.getState().initialize();
    }
  }, [status]);

  return <>{children}</>;
}

export function App() {
  return (
    <ApiClientProvider client={api}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthInitializer>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/join/:id" element={<JoinPage />} />
              {/*
                Shopify → Portal SSO landing. Receives a one-time token via
                `?token=...`, posts it to the API to consume, and then behaves
                like any other authenticated session. Public by design —
                auth state is established by the consume call.
              */}
              <Route path="/sso/shopify" element={<SsoShopifyPage />} />

              {/* Protected routes — auth required */}
              <Route element={<ProtectedRoute />}>
                <Route element={<PortalLayout />}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  {/* Notifications — user-scoped, no extra RBAC needed */}
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route
                    path="settings/notifications"
                    element={<NotificationPreferencesPage />}
                  />
                  <Route path="profile" element={<ProfilePage />} />
                </Route>
              </Route>

              {/* Orders — requires order permission */}
              <Route element={<ProtectedRoute requiredPermissions={['order:list', 'order:manage']} />}>
                <Route element={<PortalLayout />}>
                  <Route path="orders" element={<PortalOrdersPage />} />
                  <Route path="orders/new" element={<CreateOrderPage />} />
                  <Route path="orders/:id" element={<PortalOrderDetailPage />} />
                </Route>
              </Route>

              {/* Catalog & Products — requires catalog/product permission */}
              <Route element={<ProtectedRoute requiredPermissions={['catalog:read', 'product:list', 'product:manage']} />}>
                <Route element={<PortalLayout />}>
                  <Route path="catalog" element={<CatalogPage />} />
                  <Route path="catalog/:slug" element={<ProductDetailPage />} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route
                    path="custom-products"
                    element={<ProductsPage surface="custom" />}
                  />
                  <Route path="products/:id" element={<MyProductDetailPage />} />
                  <Route
                    path="custom-products/:id"
                    element={<MyProductDetailPage />}
                  />
                </Route>
              </Route>

              {/* My Designs — merchant library of apparel designs. Read
                  surface needs design:read; spawn / delete are gated on
                  the server with design:create + design:manage. */}
              <Route element={<ProtectedRoute requiredPermissions={['design:read', 'design:manage']} />}>
                <Route element={<PortalLayout />}>
                  <Route path="my-designs" element={<MyDesignsPage />} />
                  <Route path="my-designs/:id" element={<MyDesignDetailPage />} />
                </Route>
              </Route>

              {/* Stores — requires connection permission */}
              <Route element={<ProtectedRoute requiredPermissions={['connection:list', 'connection:manage']} />}>
                <Route element={<PortalLayout />}>
                  <Route path="stores" element={<StoresPage />} />
                </Route>
              </Route>

              {/* Billing hub — Subscription / Wallet / Payment methods /
                  Payments in one tabbed page. The route guard accepts ANY
                  billing-related permission; individual tabs are further
                  filtered inside the page. */}
              <Route element={<ProtectedRoute requiredPermissions={BILLING_PAGE_PERMISSIONS} />}>
                <Route element={<PortalLayout />}>
                  <Route path="billing" element={<PortalBillingPage />} />
                  {/* Legacy standalone routes — redirect into the hub while
                      preserving query params (Stripe return statuses, list
                      filters, notification deep-links). */}
                  <Route path="payments" element={<BillingTabRedirect tab="payments" />} />
                  <Route path="wallet" element={<BillingTabRedirect tab="wallet" />} />
                  <Route path="payment-methods" element={<BillingTabRedirect tab="payment-methods" />} />
                  <Route path="subscription" element={<BillingTabRedirect tab="subscription" />} />
                  {/* Legacy ACH page (Phase 14 sunset) → wallet tab. */}
                  <Route path="billing-methods" element={<BillingTabRedirect tab="wallet" />} />
                  {/* Env-example Checkout return URLs. Without these, Stripe
                      lands on a 404 that bounces to /login and looks like a
                      failed payment even though the webhook credited the
                      wallet. Preserve session_id for the hub to verify. */}
                  <Route
                    path="wallet/top-up/success"
                    element={
                      <BillingTabRedirect tab="wallet" extra={{ topup: 'success' }} />
                    }
                  />
                  <Route
                    path="wallet/top-up/cancel"
                    element={
                      <BillingTabRedirect
                        tab="wallet"
                        extra={{ topup: 'cancelled' }}
                      />
                    }
                  />
                  <Route
                    path="billing/subscription/success"
                    element={
                      <BillingTabRedirect
                        tab="subscription"
                        extra={{ changed: '1' }}
                      />
                    }
                  />
                  <Route
                    path="billing/subscription/cancel"
                    element={
                      <BillingTabRedirect tab="subscription" extra={{ cancelled: '1' }} />
                    }
                  />
                </Route>
              </Route>

              {/* Fulfillment settings — read needs settings:read; writes are
                  gated in-page + server-side with settings:manage */}
              <Route element={<ProtectedRoute requiredPermissions={['settings:read', 'settings:manage']} />}>
                <Route element={<PortalLayout />}>
                  <Route path="settings/fulfillment" element={<FulfillmentSettingsPage />} />
                  <Route path="settings/branding" element={<BrandingSettingsPage />} />
                  <Route path="settings/designer" element={<DesignerSettingsPage />} />
                </Route>
              </Route>

              {/* Organization settings — requires org update permission */}
              <Route element={<ProtectedRoute requiredPermissions={['organization:update', 'organization:manage']} />}>
                <Route element={<PortalLayout />}>
                  <Route path="settings/organization" element={<OrganizationSettingsPage />} />
                </Route>
              </Route>

              {/* Team management — requires member read permission */}
              <Route element={<ProtectedRoute requiredPermissions={['member:list', 'member:manage']} />}>
                <Route element={<PortalLayout />}>
                  <Route path="settings/team" element={<TeamPage />} />
                  <Route path="settings/team/:handle" element={<MemberDetailPage />} />
                </Route>
              </Route>

              {/* Catch-all — redirect to login (not dashboard, to avoid loops) */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </AuthInitializer>
        </BrowserRouter>
      </QueryClientProvider>
    </ApiClientProvider>
  );
}
